import React from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface PermissionGateProps {
  route: string;  // Changed from menuId: number to route: string
  action: 'view' | 'create' | 'edit' | 'delete' | 'approve';
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Generic permission gate component that shows/hides content based on user permissions
 * 
 * @param route - The route path to check permissions for (e.g., '/customers')
 * @param action - The specific action to check (view, create, edit, delete, approve)
 * @param children - Content to show if user has permission
 * @param fallback - Optional content to show if user doesn't have permission
 */
export function PermissionGate({ route, action, children, fallback }: PermissionGateProps) {
  const { userMenuPermissions } = useAuth();

  // Find the permission for this route
  const permission = userMenuPermissions.find(p => p.route === route && p.isActive);

  // Check if user has the required permission using a type-safe approach
  let hasPermission = false;
  if (permission) {
    switch (action) {
      case 'view':
        hasPermission = permission.canView;
        break;
      case 'create':
        hasPermission = permission.canCreate;
        break;
      case 'edit':
        hasPermission = permission.canEdit;
        break;
      case 'delete':
        hasPermission = permission.canDelete;
        break;
      case 'approve':
        hasPermission = permission.canApprove;
        break;
    }
  }

  // Show children if user has permission, otherwise show fallback or nothing
  if (hasPermission) {
    return <>{children}</>;
  }

  return fallback ? <>{fallback}</> : null;
}
